import {
  matchRoutes,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { useEffect, useState } from 'react';
import {
  fetchBusinessCategories,
  selectBusiness,
} from '@app/redux/features/business';
import { getUserWithBusinesses } from '@app/services/user';
import { ActionTypes } from '@app/redux/types';
import { toast } from 'react-toastify';
import { SubscriptionInterface } from '@app/models/subscription';
import { ImpersonationBanner } from '../ImpersonationBanner/ImpersonationBanner';

const pageVariants = {
  initial: {
    opacity: 0,
  },
  in: {
    opacity: 1,
  },
  out: {
    opacity: 0,
  },
};

const pageTransition = {
  type: 'tween',
  ease: 'linear',
  duration: 0.5,
};

export const AnimationLayout = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const [isConnected, setIsConnected] = useState(true);
  const auth = useAppSelector((state) => state.auth);
  const business = useAppSelector((state) => state.business);
  const location = useLocation();

  const excludePath = matchRoutes(
    [
      { path: 'forgot-password' },
      { path: 'reset-password/:token' },
      { path: '/validate-email/:token' },
      { path: '/leave-review/:requestId' },
      { path: '/leave-review/:requestId/thank-you' },
      { path: '/leave-review/:requestId/need-improvement' },
      { path: '/sign-up-confirmation' },
      { path: '/welcome' },
    ],
    location.pathname
  );

  // To avoid redirecting to home path when refresh page
  const doNotRedirectHomePath = matchRoutes(
    [
      { path: '/ratings/*' },
      { path: '/campaigns/*' },
      { path: '/integrations/*' },
      { path: '/automations/*' },
      { path: '/admin/*' },
    ],
    location.pathname
  );

  const adminPath = matchRoutes(
    [
      { path: '/admin/*' },
    ],
    location.pathname
  );

  useEffect(() => {
    const hasAccessToken = localStorage.getItem('accessToken');
    const hasUserPlanTypes = localStorage.getItem('userPlanTypes');
    const redirectBusinessIdParam = searchParams.get('redirectBusinessId');

    // Call user/me endpoint if:
    // 1. Has access token AND no user data loaded yet, OR
    // 2. Has access token AND user data but no plan types in localStorage
    if (hasAccessToken && ((!auth.user && !auth.isLoading) || (auth.user && !hasUserPlanTypes))) {
      dispatch({ type: 'auth/setLoading', payload: true });
      getUserWithBusinesses()
        .then((res) => {
          dispatch({ type: ActionTypes.AUTH_SAVE, payload: res?.data?.user });
          dispatch({
            type: ActionTypes.BUSINESS_LOAD_LIST,
            payload: res?.data?.businesses,
          });

          // Save only plan types to localStorage for easier access
          if (res?.data?.user?.subscriptions) {
            const planTypes = res.data.user.subscriptions.map((subscription: SubscriptionInterface) => ({
              businessId: subscription.businessId,
              planType: subscription.plan?.type
            }));
            localStorage.setItem('userPlanTypes', JSON.stringify(planTypes));
          }

          if (excludePath) {
            return;
          }

          if (res?.data?.businesses && !adminPath) {    // todo: maybe it is required to select business even for admin - check
            if (res?.data?.businesses?.length > 1) {
              // Check if redirected from OAuth with business ID - use this ID to autoselect business
              let redirectBusinessId = redirectBusinessIdParam;

              // Empty if business ID (from redirect) is not valid (e.g. business was deleted or user is not member of this business
              if (redirectBusinessId && !res?.data?.businesses?.find((b: { id: string | null }) => b.id === redirectBusinessId)) {
                redirectBusinessId = null;
              }

              if (redirectBusinessId) {
                // To not redirect to business select after redirect from OAuth
                dispatch(selectBusiness({ id: redirectBusinessId }));
              } else {
                // No predefined business ID - redirect to selection page
                navigate('/select-business');
              }
            }

            if (res?.data?.businesses?.length === 1) {
              dispatch(selectBusiness({ id: res?.data?.businesses[0].id }));

              // Do not redirect some routes. For some it is required (redirect to integrations after google OAuth); for some just annoying
              if (!doNotRedirectHomePath) {
                navigate('/');
              }
            }
          }
        })
        .catch(() => {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('googleAccessToken');
          localStorage.removeItem('userPlanTypes');
          dispatch({ type: 'auth/setLoading', payload: false });
        })
        .finally(() => {
          if (redirectBusinessIdParam) {
            // Remove param from URL
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('redirectBusinessId');
            setSearchParams(newParams, { replace: true });
          }
        })
      ;
    }

  }, [auth.user, auth.isLoading, dispatch, navigate, excludePath]);

  useEffect(() => {
    const handleOnline = () => setIsConnected(true);
    const handleOffline = () => setIsConnected(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isConnected) {
      toast.warn('You are offline');
    }
  }, [isConnected]);

  useEffect(() => {
    if (business.categories?.length === 0 && !business.isCategoriesFetched) {
      dispatch(fetchBusinessCategories());
    }
  }, [business.categories?.length, business.isCategoriesFetched, dispatch]);

  return (
    <>
      <ImpersonationBanner />
      <motion.div
        key={pathname}
        initial="initial"
        animate="in"
        variants={pageVariants}
        transition={pageTransition}
      >
        <Outlet />
      </motion.div>
    </>
  );
};
